import { useContext, useEffect, useRef } from "react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";

import NeighborhoodsComponent from "./NeighborhoodsComponent";
import OImages from "../../shared/OImages";

gsap.registerPlugin(ScrollTrigger);

const NeighborhoodsParent = ({ imgSrc, destinations }: { imgSrc: any; destinations: any }) => {
	const { isIpad, isReady } = useContext(screenSizeContext);
	const neighborhoodsParent = useRef<any>(null);
	const pinnedCircle = useRef<any>(null);
	const tl = useRef<gsap.core.Timeline>(null);
	const currentTl = useRef<any>(null);
	const offset = useRef<number>(0);
	const viewSetting = useRef<{ last: boolean; enter: boolean, first: boolean }>({ last: false, enter: false, first: false });
	const currentIndex = useRef<number>(1);
	const stickyLinks = useRef<any>(null);
	const stickyListOpen = useRef<any>(false);
	const stickyListTl = useRef<any>(null);

	const handleWheel = (e: any) => {
		if(!isIpad && viewSetting.current.enter && !currentTl.current?.isActive()) {
			if(offset.current >= 400 && currentIndex.current < destinations.length) {
				currentIndex.current = currentIndex.current + 1;
				currentTl.current = tl.current?.tweenTo(`neighborhood-${currentIndex.current}`);
				offset.current = 0;
				return;
			}

			if(offset.current <= -400 && currentIndex.current > 1) {
				currentIndex.current = currentIndex.current - 1;
				currentTl.current = tl.current?.tweenTo(`neighborhood-${currentIndex.current}`);
				offset.current = 0;
				viewSetting.current.last = false;

				if(!stickyListOpen.current) {
					const dropdown = neighborhoodsParent.current?.querySelector(".sticky-neighborhoods ul");
					const dropdownList = neighborhoodsParent.current?.querySelectorAll(".sticky-neighborhoods ul li");

					const duration = 0.5;
					const ease = "power2.inOut";

					gsap.to(dropdown, {
						duration,
						ease,
						stagger: 0.1,
						y: `${-3 * (currentIndex.current - 1)}rem`,
					});
					
					dropdownList.forEach((el: any, i: number) => {
						if(i === currentIndex.current - 1) el.classList.add("active"); 
						else el.classList.remove("active");
					});
				}

				return;
			}

			if(
				currentIndex.current === destinations.length && 
				Math.sign(e.deltaY) === 1 && 
				viewSetting.current.last
			) {
				window.lenis?.startScroll?.();
			}

			if(currentIndex.current === 1 && 
				Math.sign(e.deltaY) === -1 && 
				viewSetting.current.first
			) {
				window.lenis?.startScroll?.();
			}

			offset.current = Math.max(-400, Math.min(400, offset.current + e.deltaY));
		}
	};

	const handleStickyOpen = () => {
		if(stickyListOpen.current) return;

		if(stickyListTl.current) stickyListTl.current.kill();
		stickyListTl.current = gsap.timeline();
	
		stickyListTl.current
		.to(".sticky-neighborhoods ul", {
			duration: 0.7,
			y: "0rem",
			ease: "power4.out",
		})
		.to(".sticky-neighborhoods button", {
			duration: 0.7,
			ease: "power4.out",
			rotate: "180deg",
		}, "<")
		.to(".sticky-neighborhoods", {
			duration: 0.7,
			ease: "power2.inOut",
			height: "auto",
		}, "-=0.3");

		stickyListOpen.current = true;
	};
	
	const handleStickyClose = ()=> {
		if(stickyListTl.current && stickyListOpen.current) {
			stickyListTl.current.kill();
			stickyListTl.current = gsap.timeline();
		
			stickyListTl.current
			.to(".sticky-neighborhoods", {
				duration: 0.7,
				ease: "power2.inOut",
				height: "3rem",
			})
			.to(".sticky-neighborhoods ul", {
				duration: 0.7,
				y: `${-3 * (currentIndex.current - 1)}rem`,
				ease: "power4.out",
			}, "-=0.3")
			.to(".sticky-neighborhoods button", {
				duration: 0.7,
				ease: "power4.out",
				rotate: "0deg",
			}, "<")
			.call(() => {
				stickyListOpen.current = false;
			});
		}
	}

	const handleStickyListClick = (index: number) => {
		window.lenis?.stopScroll?.();
		window.lenis?.scrollTo(neighborhoodsParent.current, { force: true, duration: 0.75 });

		const increaseDuration = (Math.abs(index - currentIndex.current) / destinations.length) * 1;
		currentTl.current = tl.current?.tweenFromTo(`neighborhood-${currentIndex.current}`, `neighborhood-${index + 1}`, { duration: 1.25 + increaseDuration, ease: "power4.inOut" });
		currentIndex.current = index + 1;

		const dropdownList = neighborhoodsParent.current?.querySelectorAll(".sticky-neighborhoods ul li");
		dropdownList.forEach((el: any, i: number) => {
			if(i === index) el.classList.add("active"); 
			else el.classList.remove("active");
		});

		handleStickyClose();
	}

	useEffect(()=> {
		if (!isReady) return;

		if(destinations?.length && imgSrc) {
			if(!isIpad) {
				tl.current = gsap.timeline({ paused: true });
	
				const children = neighborhoodsParent.current?.querySelectorAll(".neighborhoods");
				const dropdown = neighborhoodsParent.current?.querySelector(".sticky-neighborhoods ul");
				const dropdownList = neighborhoodsParent.current?.querySelectorAll(".sticky-neighborhoods ul li");
				destinations.forEach((_: any, index: number)=> {
					const onReverseComplete = () => {
						// window.lenis?.startScroll?.();
						viewSetting.current.first = true;
					}
					const onComplete = () => {
						viewSetting.current.first = false;
					}
					const childTimeline = gsap.timeline({
						onReverseComplete: index === 1 ? onReverseComplete: undefined,
						onComplete: index === 1 ? onComplete: undefined
					});
	
					const child = children[index] as HTMLElement;
					const cols = child.querySelectorAll(".neighborhoods-col");
					const content = child.querySelectorAll(".neighborhoods_content");
					const logo = child.querySelector(".neighborhoods_circle_Logo div");
					const link = child.querySelector(".neighborhoods_circle_line a");
	
					const duration = 0.5;
					const ease = "power2.inOut";
					
					childTimeline
					.set(child, { pointerEvents: "all" })
					.to(cols, {
						duration: 0.75,
						ease: "power4.inOut",
						stagger: 0.1,
						y: "0vh",
					})
					.to(content, {
						duration,
						ease,
						y: "0vh",
					}, "<")
					.to([logo, link], {
						duration,
						ease,
						stagger: 0.1,
						y: "0"
					}, "<");

					if(index === 0) {
						childTimeline.call(() => {
							viewSetting.current.first = true;
						});
					}
					if(index === destinations.length - 1) {
						childTimeline.call(() => {
							window.lenis?.startScroll?.();
							viewSetting.current.last = true;
						});
					}
					
					tl.current
					?.addLabel(`neighborhood-${index}`)
					if(index > 0) {
						const prevTimeline = gsap.timeline();
	
						const child = children[index-1] as HTMLElement;
						const cols = child.querySelectorAll(".neighborhoods-col");
						const content = child.querySelectorAll(".neighborhoods_content");
						const logo = child.querySelector(".neighborhoods_circle_Logo div");
						const link = child.querySelector(".neighborhoods_circle_line a");
	
						prevTimeline
						.set(child, { pointerEvents: "all" })
						.to(cols, {
							duration: 0.75,
							ease: "power4.inOut",
							stagger: 0.1,
							y: "-105vh",
							onStart: () => {
								if(!stickyListOpen.current) {
									gsap.to(dropdown, {
										duration,
										ease,
										stagger: 0.1,
										y: `${-3 * index}rem`,
									});
									dropdownList.forEach((el: any, i: number) => {
										if(i === index) el.classList.add("active"); 
										else el.classList.remove("active");
									});
								}
							}
						})
						.to(content, {
							duration,
							ease,
							y: "-105vh",
						}, "<")
						.to([logo, link], {
							duration,
							ease,
							stagger: 0.1,
							y: "-105%",
						}, "<");
	
						tl.current
						?.add(prevTimeline);
					}
					tl.current
					?.add(childTimeline, index > 0 ? `-=${duration}` : undefined);
				});

				const enterTrigger = ScrollTrigger.create({
					trigger: neighborhoodsParent.current,
					start: "top bottom",
					end: "bottom bottom",
					onEnter: () => {
						if(!viewSetting.current.enter) {
							tl.current?.tweenTo("neighborhood-1", { duration: 0.7 });
							viewSetting.current.enter = true;
						}
					}
				});
	
				const trigger = ScrollTrigger.create({
					trigger: neighborhoodsParent.current,
					start: "top top",
					end: "bottom bottom",
					onEnter: () => {
						window.lenis?.scrollTo(neighborhoodsParent.current, { immediate: true, force: true });
						window.lenis?.stopScroll?.();
						gsap.to(".navbar_section", { y: "-105%" });
					},
					onEnterBack: () => {
						if(currentIndex.current > 1) {
							window.lenis?.scrollTo(neighborhoodsParent.current, { immediate: true, force: true });
							window.lenis?.stopScroll?.();
						}
					}
				});
	
				return () => {
					enterTrigger?.kill();
					trigger?.kill();
					tl.current?.revert();
				};
			} else {
				const trigger = ScrollTrigger.create({
					trigger: neighborhoodsParent.current,
					start: "top-=200px top",
					end: "bottom bottom",
					scrub: 1,
					pin: pinnedCircle.current,
					pinSpacing: false,
				});

				// ScrollSmoother only refreshes on desktop. Re-measure pin positions if content grows after
				// they were taken (info rows, fonts on a cold load), otherwise a logo could pin too early.
				let refreshTimer: ReturnType<typeof setTimeout> | undefined;
				const refresh = () => {
					clearTimeout(refreshTimer);
					refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 150);
				};
				// Observe the page wrapper, not body: html/body are height: 100% so body never resizes.
				const observer = new ResizeObserver(refresh);
				observer.observe(neighborhoodsParent.current.parentElement ?? neighborhoodsParent.current);

				return () => {
					clearTimeout(refreshTimer);
					observer.disconnect();
					trigger.kill();
				};
			}
		}
	}, [destinations?.length, imgSrc, isIpad, isReady]);

	const handleClickOutside = (event: MouseEvent) => {
		const wrapper = document.querySelector(".sticky-neighborhoods") as any

		if (
			stickyLinks.current &&
			!stickyLinks.current.contains(event.target)
		) {
			handleStickyClose()
		}

		if (wrapper && !wrapper.contains(event.target)) {
			handleStickyClose()
		}
	}

	useEffect(() => {
		if (!isReady || isIpad) return;

		document.addEventListener("click", handleClickOutside)
		return () => {
			document.removeEventListener("click", handleClickOutside)
		}
	}, [isIpad, isReady])

	return (
		<div
			onWheel={handleWheel}
			ref={neighborhoodsParent}
			className={`neighborhoods-parent || relative min-h-screen mt-[8.75rem] pb-[3.75rem] lg:mt-0 lg:pb-0 lg:h-screen lg:min-h-0 bg-white z-10`}
		>
			<div 
				ref={pinnedCircle}
				className="neighborhoods-circle || w-[25.3125rem] max-w-full flex items-center justify-center mx-auto lg:max-w-none lg:absolute lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:mx-0"
			>
				<div className="w-[90%] -mb-[100%] lg:w-full lg:-mb-0">
					<OImages />
				</div>
			</div>
			{destinations?.map((item: any, index: number) => (
				<NeighborhoodsComponent
					key={index}
					parent={neighborhoodsParent}
					index={index}
					data={item}
				/>
			))}
			{
				!isIpad && (
					<div
						onClick={handleStickyOpen}
						ref={stickyLinks}
						className="sticky-neighborhoods || px-[1.25rem] flex justify-between absolute bottom-[1.5rem] w-[20rem] h-[3rem] left-1/2 -translate-x-1/2 bg-oBlue-100 pointer-events-auto overflow-hidden"
					>
						<ul className="text-white">
							{destinations?.map((item: any, index: number) => {
								return (
									<li 
										key={index}
										className={`${index === 0 ? "active" : ""} h-[3rem] flex items-center font-semibold text-[1.25rem] cursor-pointer`}
										onClick={()=> handleStickyListClick(index)}
									>
										<span>{item.name.en}</span>
									</li>
								)
							})}
						</ul>
						<div onClick={()=> {
							if(stickyListOpen.current) handleStickyClose();
							else handleStickyOpen();
						}} 
						className="h-[3rem] flex items-center justify-center">
							<button
								type="button"
								aria-label="Toggle neighborhoods list"
								className="p-[0.3rem] flex items-center justify-center size-[2rem] bg-white"
							>
								<svg viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
									<path d="M6.05377 0.21967C5.76087 -0.0732233 5.286 -0.0732233 4.99311 0.21967L0.220137 4.99264C-0.0727566 5.28553 -0.0727566 5.76041 0.220137 6.0533C0.51303 6.34619 0.987904 6.34619 1.2808 6.0533L5.52344 1.81066L9.76608 6.0533C10.059 6.34619 10.5338 6.34619 10.8267 6.0533C11.1196 5.76041 11.1196 5.28553 10.8267 4.99264L6.05377 0.21967ZM5.52344 1.75H6.27344V0.75H5.52344H4.77344V1.75H5.52344Z" fill="#0e223e"/>
								</svg>
							</button>
						</div>
					</div>
				)
			}
		</div>
	);
};

export default NeighborhoodsParent;